<template>
  <div class="event-form event__review" v-if="newEvent">
    <section>
      <div v-if="showTitle" class="event-form--section-title event-review--section-title">
        <span>Event Preview</span>
        Review your event details
      </div>
      <div class="event-form--content">
        <section class="event-review-section event-review-section--basics">
          <div class="event-review-basics__title">
            {{ newEvent.name || 'No event name entered' }}
          </div>
          <div class="event-review-basics__details">
            <IconAccount /> {{ accountName }} <IconLocation /> {{ locationName }}
          </div>
          <div class="event-review-basics__dates">
            <span>Start: {{ startDate }} {{ startTimeZone }}</span>
            <span class="divider">|</span>
            <span>End: {{ endDate }} {{ endTimeZone }}</span>
          </div>
        </section>

        <section
          v-if="newEvent.booking_id"
          class="event-review-section event-review-section__booking"
        >
          <div class="event-review-section__title">
            <span>Booking</span>
          </div>
          <div class="event-review-section__content">
            <label v-if="usingCredits">Booking Credits:</label>
            <label v-else>Booking Name:</label>

            <div class="booking_details">
              <div class="booking_details-name">
                {{ bookingCreditsType }}
                <span v-if="bookingCreditsRemaining">
                  | {{ bookingCreditsRemaining }} Remaining
                </span>
              </div>
              <div v-if="usingCredits" class="credits-explainer">1 Credit = 1 Sensor Days</div>
            </div>
          </div>
        </section>

        <div class="event-chips-container">
          <section class="event-review-section event-review-section__chips">
            <div class="event-review-section__title">
              <span>Event Type</span>
            </div>
            <div class="event-review-section__content">
              <template v-if="newEvent.type">
                <Chip :key="newEvent.type" :isStatic="true">
                  {{
                    eventsStore.eventList?.event_type.find((evt) => evt.id === newEvent?.type)?.name
                  }}
                </Chip>
              </template>
              <template v-else> No types chosen </template>
            </div>
          </section>

          <section class="event-review-section event-review-section__chips">
            <div class="event-review-section__title">
              <span>Verticals</span>
            </div>
            <div class="event-review-section__content">
              <template v-if="newEvent.verticals.length">
                <Chip v-for="vertId in newEvent.verticals" :key="vertId" :isStatic="true">
                  {{ eventsStore.eventList?.vertical.find((evt) => evt.id === vertId)?.name }}
                </Chip>
              </template>
              <template v-else> No verticals chosen </template>
            </div>
          </section>
        </div>

        <section class="event-review-section">
          <div class="event-review-section__title">
            <span>Schedule</span>
          </div>
          <div class="event-review-section__content">
            <ul class="date-overrides" :class="{ 'date-overrides--overnight': isOvernight }">
              <li class="date-overrides__header">
                <span><template v-if="isOvernight">Start </template>Date</span>
                <span>Start Time</span>
                <span v-if="isOvernight">End Date</span>
                <span>End Time</span>
              </li>
              <li v-for="date in newEvent.dates" :key="date.day">
                <span :class="{ inactive: !dateIncludes?.includes(date.day) }">{{
                  dayjs(date.day).format('ddd, MMM DD, YYYY')
                }}</span>
                <span v-if="dateIncludes?.includes(date.day)">{{
                  dayjs(date.from).format('hh:mm A')
                }}</span>
                <span v-if="dateIncludes?.includes(date.day) && isOvernight">
                  <template v-if="date.isDateOvernight">
                    {{ dayjs(date.day).add(1, 'day').format('ddd, MMM DD, YYYY') }}
                  </template>
                </span>
                <span v-if="dateIncludes?.includes(date.day)">{{
                  dayjs(date.to).format('hh:mm A')
                }}</span>
              </li>
            </ul>
          </div>
        </section>

        <EventReviewZones :newEvent="newEvent" :sensorDistanceOptions="sensorDistanceOptions" />

        <section class="event-review-section">
          <div class="event-review-section__title">
            <span>Clusters ({{ newEvent.clusters.length }})</span>
          </div>

          <div
            v-if="newEvent.clusters.length"
            class="event-review-section__content event-review-section__split"
          >
            <Tabs value="0">
              <div class="split-section__list">
                <div class="split-section__list-header">Cluster</div>
                <TabList>
                  <Tab
                    v-for="(cluster, index) in newEvent.clusters"
                    :key="index"
                    :value="`${index}`"
                  >
                    {{ cluster.name }} ({{ cluster.zones?.length }})
                  </Tab>
                </TabList>
              </div>
              <div class="split-section__details">
                <div class="split-section__details-header">Zone Details</div>

                <TabPanels>
                  <TabPanel
                    v-for="(cluster, index) in newEvent.clusters"
                    :key="index"
                    :value="`${index}`"
                  >
                    <ul class="zone-list">
                      <li v-for="zone in cluster.zones" :key="zone.name">
                        {{ zone.name }}
                        <Chip
                          >{{ zone.sensors.length }} Sensor<template
                            v-if="zone.sensors.length !== 1"
                            >s</template
                          ></Chip
                        >
                      </li>
                    </ul>
                  </TabPanel>
                </TabPanels>
              </div>
            </Tabs>
          </div>
          <div v-else class="event-review-section__content">
            <div class="event-review__empty-message">No clusters created</div>
          </div>
        </section>

        <section class="event-review-section">
          <div class="event-review-section__title">
            <span>Notifications</span>
            Event details will be sent to these email(s) or phone number(s)
          </div>
          <div
            v-if="newEvent.notifications?.length"
            class="event-review-section__content event-review-section__table"
          >
            <div>Who to notify</div>
            <table>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Phone Number</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(notification, index) in newEvent.notifications" :key="index">
                  <td>{{ notification.email }}</td>
                  <td>{{ notification.phone_number }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="event-review-section__content">
            <div class="event-review__empty-message">No notifications set</div>
          </div>
        </section>
      </div>
    </section>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import toObject from 'dayjs/plugin/toObject';
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(toObject);

import { Tabs, TabList, Tab, TabPanels, TabPanel } from 'primevue';

import Chip from '@/components/global/Chip.vue';
import EventReviewZones from './EventReviewZones.vue';

import { getSensorRangeOptions } from '@/utils/api';

import type { EventTypeNew } from '@/types/Event';
import type { SensorDistanceSettingType } from '@/types/Sensor';

import IconAccount from '@/icons/users/IconAccount.svg?component';
import IconLocation from '@/icons/IconLocation.svg?component';

import { useEventsStore } from '@/stores/events';
const eventsStore = useEventsStore();

import { useUserStore } from '@/stores/user';
const userStore = useUserStore();

import { useAccountsStore } from '@/stores/accounts';
const accountsStore = useAccountsStore();

const newEvent = defineModel<EventTypeNew>('newEvent');
const dateIncludes = defineModel<string[]>('dateIncludes');

defineProps<{
  showTitle: boolean;
}>();

const accountName = computed(() => {
  if (newEvent.value?.account_id) {
    const acctMatch = accountsStore.accountsDataSimple.find(
      (val) => val.id === newEvent.value?.account_id,
    );
    return acctMatch?.display_name || '';
  }
  return 'No account chosen';
});

const locationName = computed(() => {
  if (newEvent.value?.timezone_id) {
    const locMatch = eventsStore.eventList?.location.find(
      (val) => val.id === newEvent.value?.timezone_id,
    );
    return locMatch?.name || '';
  }
  return 'No location chosen';
});

const startDate = computed(() => {
  if (!newEvent.value || !newEvent.value.event_start) {
    return '';
  }

  return dayjs(newEvent.value.event_start).format('ddd, MMM DD, YYYY h:mmA');
});

const startTimeZone = computed(() => {
  if (!newEvent.value || !newEvent.value.event_start_tz) {
    return '';
  }

  return newEvent.value.event_start_tz;
});

const endDate = computed(() => {
  if (!newEvent.value || !newEvent.value.event_end) {
    return '';
  }

  return dayjs(newEvent.value.event_end).format('ddd, MMM DD, YYYY h:mmA');
});

const endTimeZone = computed(() => {
  if (!newEvent.value || !newEvent.value.event_end_tz) {
    return '';
  }

  return newEvent.value.event_end_tz;
});

const sensorDistanceOptions = ref<SensorDistanceSettingType[]>([]);

if (newEvent.value?.zones.length) {
  const firstSensor = newEvent.value.zones[0].sensors[0].id;
  if (firstSensor) {
    getSensorRangeOptions(userStore.accessToken, userStore.isStaff, firstSensor).then((resp) => {
      sensorDistanceOptions.value = resp;
    });
  }
}

const isOvernight = computed(() => {
  if (newEvent.value?.event_start && newEvent.value?.event_end) {
    const start = dayjs(newEvent.value?.event_start).hour();
    const end = dayjs(newEvent.value?.event_end).hour();

    return start >= end;
  }
  return false;
});

const sensorDays = computed(() => {
  const sensorsCount = newEvent.value?.zones.length || 0;
  const daysCount =
    dayjs(newEvent.value?.event_end)
      .add(1, 'day')
      .diff(dayjs(newEvent.value?.event_start), 'day') || 0;

  return sensorsCount * daysCount;
});

const bookingCreditsType = computed(() => {
  if (!newEvent.value || !newEvent.value.booking) {
    return '';
  }

  if (!newEvent.value.booking?.campaign_name.includes('Credits')) {
    return newEvent.value?.booking?.campaign_name;
  }

  if (newEvent.value.booking.campaign_name.includes('Standard Credits')) {
    return `Using ${sensorDays.value} Standard Credits`;
  }

  if (newEvent.value.booking.campaign_name.includes('Premium Credits')) {
    return `Using ${sensorDays.value} Premium Credits`;
  }

  return '';
});

const usingCredits = computed(() => {
  return bookingCreditsType.value.includes('Credits');
});

const bookingCreditsRemaining = computed(() => {
  if (bookingCreditsType.value.includes('Credits')) {
    if (newEvent.value && newEvent.value.booking) {
      return newEvent.value?.booking?.days - sensorDays.value;
    }
  }
  return 0;
});
</script>
